import React, { useState, memo } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ChevronDown, ChevronUp, MessageCircle, Crown, Heart, Gem, GraduationCap, Headphones, Bus, Brain, Download, FileText, Map as MapIcon } from 'lucide-react';
import { LanguageSelector } from './LanguageSelector';
import { ShareButtons } from './ShareButtons';
import { COURT_RUSH_BRIEF_MD, downloadText } from './treasury/claudeDesignDownloads';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations';
export const Footer = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const {
    language
  } = useLanguage();
  const t = translations[language];
  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };
  return <footer className="bg-black text-white relative overflow-hidden" data-id="element-1244" data-mp="29ydxq3q81abh">
      <div className="absolute inset-0 bg-gradient-to-t from-amber-900/10 to-transparent opacity-20" data-id="element-1245" data-mp="2eyt9wryrobrv"></div>
      <div className="container mx-auto px-4 py-20 relative z-10" data-id="element-1246" data-mp="3l8qx26bccnsi">
        {/* Founding Members CTA */}
        <div className="max-w-4xl mx-auto mb-16 border border-amber-400/50 bg-amber-400/[0.06] p-8 sm:p-10" data-id="element-1247" data-mp="3gbinuv867u3e">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-8" data-id="element-1248" data-mp="3v8hrk7zwj2sl">
            <div data-id="element-1249" data-mp="3q7u1bojhypzf">
              <p className="text-amber-400 text-[10px] tracking-[0.3em] uppercase font-medium mb-3 flex items-center gap-2" data-id="element-1250" data-mp="3q7bw42eux1fd">
                <Crown className="w-3.5 h-3.5" data-id="element-1251" data-mp="80ntcj7a4qcv" />
                Founding Members
              </p>
              <h4 className="font-serif text-2xl sm:text-3xl text-white leading-tight mb-3" data-id="element-1252" data-mp="3230j4ucx8yg">
                Put your name on the court before the doors open.
              </h4>
              <p className="text-white/60 text-sm font-light leading-relaxed max-w-xl" data-id="element-1253" data-mp="cuiiw39kfzjv">
                We&rsquo;re assembling a small founding cohort behind Canada&rsquo;s
                most ambitious immersive experience. Book a 25-minute call with
                Ash Singh, no big cheque required.
              </p>
            </div>
            <a href="/founding-members" className="shrink-0 inline-flex items-center justify-center gap-3 bg-amber-400 hover:bg-amber-300 text-black px-8 py-4 text-[11px] tracking-[0.22em] uppercase font-bold transition-colors duration-150" data-id="element-1254" data-mp="ytxylo122j20">
              Become a Founding Member
              <ArrowRight className="w-4 h-4" data-id="element-1255" data-mp="1ckknkoh0kukw" />
            </a>
          </div>
        </div>
        {/* Language Selector */}
        <div className="flex justify-center mb-12" data-id="element-1256" data-mp="1hj4buvj2yigf">
          <LanguageSelector data-id="element-1257" data-mp="1mj8tiovq5j88" />
        </div>
        {/* Share Section */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="max-w-2xl mx-auto mb-20 border border-amber-400/30 p-8 bg-black/40" data-id="element-1258" data-mp="1mhxroulualjr">
          <ShareButtons layout="horizontal" title="Share This Cultural Experience" data-id="element-1259" data-mp="2agyg1b5rp7qv" />
        </motion.div>
        {/* FAQ Section */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="max-w-4xl mx-auto mb-20" data-id="element-1260" data-mp="21nbk9l1k84ho">
          <h4 className="text-center font-serif text-3xl text-amber-400 mb-12 tracking-wider uppercase" data-id="element-1261" data-mp="2kcrz7ddzrzhm">
            {t.faqTitle}
          </h4>
          <div className="space-y-4" data-id="element-1262" data-mp="2fdor1k2ptd5f">
            {t.faqQuestions.map((faq, index) => <div key={index} className="border border-amber-400/30 bg-black/40 overflow-hidden" data-id="element-1263" data-mp="18yxprf9ojqav" data-mpi={index}>
                <button onClick={() => toggleFaq(index)} className="flex justify-between items-center w-full p-6 text-left" data-id="element-1264" data-mp="2rvr9bkiumyhz">
                  <span className="text-white font-light pr-4" data-id="element-1265" data-mp="2wokda66hu2t0">
                    {faq.question}
                  </span>
                  {openFaq === index ? <ChevronUp className="w-5 h-5 text-amber-400 flex-shrink-0" data-id="element-1266" data-mp="2nuxm6td8dnq5" /> : <ChevronDown className="w-5 h-5 text-amber-400 flex-shrink-0" data-id="element-1267" data-mp="3bk8dlk8oy5rm" />}
                </button>
                {openFaq === index && <div className="px-6 pb-6 text-white/70 font-light leading-relaxed border-t border-amber-400/20 pt-4" data-id="element-1268" data-mp="31mc4exzbdkw5">
                    {faq.answer}
                  </div>}
              </div>)}
          </div>
        </motion.div>
        {/* Footer Links */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-16 border-t border-amber-400/30 pt-16" data-id="element-1269" data-mp="1v6034o173mwc">
          {/* Brand */}
          <div className="md:col-span-2" data-id="element-1270" data-mp="1nkmoo9j20j5f">
            <div className="flex items-center mb-6" data-id="element-1271" data-mp="1imv09phmbf5z">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/a1thpmaUHZUisJ3HgEhAjA/oldkhanda.png" alt="Khanda Symbol" className="w-10 h-10 mr-3 object-contain" data-id="element-1272" data-mp="1im33wcg75den" />
              <h3 className="font-serif text-2xl text-amber-400" data-id="element-1273" data-mp="1rlq6h1knwp9a">
                {t.footerTitle}
              </h3>
            </div>
            <p className="text-white/60 mb-6 max-w-md font-light leading-relaxed" data-id="element-1274" data-mp="zwwpv2qcl9z6">
              {t.footerDescription}
            </p>
          </div>
          {/* Quick Links */}
          <div data-id="element-1275" data-mp="18x1lygq3cly1">
            <h4 className="text-amber-400 font-light text-sm mb-4 tracking-[0.3em] uppercase" data-id="element-1276" data-mp="18wjj318xb9h5">
              {t.quickLinks}
            </h4>
            <ul className="space-y-3" data-id="element-1277" data-mp="l5w641k5htnq">
              {t.links.map((link, index) => <li key={index} data-id="element-1278" data-mp="g7majpuikr1l" data-mpi={index}>
                  <a href={link.href} className="text-white/60 hover:text-amber-400 transition-colors text-sm font-light" data-id="element-1279" data-mp="g6jmrqshn4pw">
                    {link.text}
                  </a>
                </li>)}
              <li data-id="element-1280" data-mp="2q0yocn1rjmni">
                <a href="/blueprint" className="text-white/60 hover:text-amber-400 transition-colors text-sm font-light" data-id="element-1281" data-mp="2q2ji4ag971kb">
                  Experience Blueprint
                </a>
              </li>
              <li data-id="element-1282" data-mp="22bxbmnlwdpcf">
                <a href="/founding-members" className="text-amber-400/85 hover:text-amber-300 transition-colors text-sm font-light flex items-center gap-2" data-id="element-1283" data-mp="22be4m1xscld6">
                  <Crown className="w-3 h-3" data-id="element-1284" data-mp="2bcvhlbfwc6xn" />
                  Become a Founding Member
                </a>
              </li>
              <li data-id="element-1285" data-mp="3mqpigl6577ss">
                <a href="/kingdom-circle" className="text-white/60 hover:text-amber-400 transition-colors text-sm font-light flex items-center gap-2" data-id="element-1286" data-mp="3mq6bfzi163tj">
                  <Crown className="w-3 h-3" data-id="element-1287" data-mp="1718ycx5yv1a0" />
                  Kingdom Circle
                </a>
              </li>
              <li data-id="element-1288" data-mp="12a0nrxswh9v9">
                <a href="/sikh-heritage-forward" className="text-white/60 hover:text-amber-400 transition-colors text-sm font-light flex items-center gap-2" data-id="element-1289" data-mp="129hf77p53xtw">
                  <Heart className="w-3 h-3" data-id="element-1290" data-mp="saruz89ru1ah" />
                  Sikh Heritage Forward
                </a>
              </li>
              <li data-id="element-1291" data-mp="jc6hhrj5pcgk">
                <a href="/treasure" className="text-white/60 hover:text-amber-400 transition-colors text-sm font-light flex items-center gap-2" data-id="element-1292" data-mp="jbobw4altlvz">
                  <Gem className="w-3 h-3" data-id="element-1293" data-mp="2dqqwbnksqds1" />
                  Treasures of the Sikh Empire
                </a>
              </li>
              <li data-id="element-1294" data-mp="28sfmhcui439t">
                <a href="/court-rush" className="text-amber-400/85 hover:text-amber-300 transition-colors text-sm font-light flex items-center gap-2" data-id="element-1295" data-mp="28rw1fn9k0yrk">
                  <Crown className="w-3 h-3" data-id="element-1296" data-mp="1mmxyin75qflo" />
                  Court Rush
                </a>
              </li>
              <li data-id="element-1297" data-mp="17u4hgxc8lrew">
                <a href="/restore-punjab" className="text-white/60 hover:text-amber-400 transition-colors text-sm font-light flex items-center gap-2" data-id="element-1298" data-mp="1cus65ccztw5y">
                  <MapIcon className="w-3 h-3" data-id="element-1299" data-mp="12xdudq20lvjz" />
                  Restore Punjab
                </a>
              </li>
              <li data-id="element-1300" data-mp="oy4njgzp8dso">
                <a href="/schools" className="text-white/60 hover:text-amber-400 transition-colors text-sm font-light flex items-center gap-2" data-id="element-1301" data-mp="oynxobj3xxw5">
                  <GraduationCap className="w-3 h-3" data-id="element-1302" data-mp="2jbng0cevh674" />
                  For Schools &amp; Curriculum Partners
                </a>
              </li>
              <li data-id="element-1303" data-mp="cmltam4xq587">
                <a href="/sponsor-a-bus" className="text-white/60 hover:text-amber-400 transition-colors text-sm font-light flex items-center gap-2" data-id="element-1304" data-mp="cm2c57n3zkv8">
                  <Bus className="w-3 h-3" data-id="element-1305" data-mp="mcx14jrqoykd" />
                  Sponsor a Bus
                </a>
              </li>
              <li data-id="element-1306" data-mp="hl6k5uyarcjz">
                <a href="/house-of-lions" className="text-white/60 hover:text-amber-400 transition-colors text-sm font-light flex items-center gap-2" data-id="element-1307" data-mp="hknd59a6q8kq">
                  <Headphones className="w-3 h-3" data-id="element-1308" data-mp="qgcsp1oyc8q7" />
                  House of Lions Podcast
                </a>
              </li>
              <li data-id="element-1309" data-mp="10cpl7w8rggjd">
                <a href="/ai-entrepreneurship" className="text-white/60 hover:text-amber-400 transition-colors text-sm font-light flex items-center gap-2" data-id="element-1310" data-mp="10c6cn65034i0">
                  <Brain className="w-3 h-3" data-id="element-1311" data-mp="324uizocxy820" />
                  Entrepreneurship and AI Opportunity
                </a>
              </li>
              <li data-id="element-1312" data-mp="is5klcrwk7dc">
                <a href="/sponsor-deck" className="text-white/60 hover:text-amber-400 transition-colors text-sm font-light flex items-center gap-2" data-id="element-1313" data-mp="dv5gv8iizgpe">
                  <Crown className="w-3 h-3" data-id="element-1314" data-mp="3wzhoyo3soo8" />
                  Sponsor Deck
                </a>
              </li>
            </ul>
          </div>
          {/* Contact */}
          <div data-id="element-1315" data-mp="11msz721q1jg2">
            <h4 className="text-amber-400 font-light text-sm mb-4 tracking-[0.3em] uppercase" data-id="element-1316" data-mp="11mkpfj0wau93">
              {t.contactUs}
            </h4>
            <a href="https://chat.whatsapp.com/Joakmqmpdd3A0riGDdBEEb?mode=wwc" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-amber-400 hover:bg-amber-500 text-black font-medium py-3 px-6 transition-colors text-sm" data-id="element-1317" data-mp="rtvcejpp58bn">
              <MessageCircle className="w-4 h-4" data-id="element-1318" data-mp="1fiy0oaeu5zf4" />
              {t.subscribe}
            </a>
          </div>
        </div>

        {/* Claude Design resource handoff */}
        <motion.details initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="max-w-4xl mx-auto mb-16 border border-amber-400/25 bg-black/40 group" data-id="element-1319" data-mp="1rtyxi5b7bnq3">
          <summary className="cursor-pointer list-none px-5 sm:px-7 py-5 flex items-center justify-between gap-5" data-id="element-1320" data-mp="2asjkbl6286xx">
            <div className="flex items-center gap-3 text-left" data-id="element-1321" data-mp="2fkuv5j2c9d59">
              <div className="w-9 h-9 shrink-0 border border-amber-400/40 bg-amber-400/10 flex items-center justify-center" data-id="element-1322" data-mp="2kjxqucwfi70k">
                <FileText className="w-4 h-4 text-amber-400" data-id="element-1323" data-mp="2pjsco21ck6er" />
              </div>
              <div data-id="element-1324" data-mp="2ugsiqcy7595v">
                <p className="text-amber-400 text-[10px] tracking-[0.3em] uppercase font-medium mb-1" data-id="element-1325" data-mp="2ui3ogib7cqzm">
                  Claude Design
                </p>
                <p className="text-white/60 text-xs sm:text-sm font-light" data-id="element-1326" data-mp="1zulo15lr6dlw">
                  Court Rush product brief and design resource
                </p>
              </div>
            </div>
            <ChevronDown className="w-4 h-4 text-amber-400/70 shrink-0 transition-transform duration-300 group-open:rotate-180" data-id="element-1327" data-mp="29st2nhskkjkq" />
          </summary>

          <div className="border-t border-amber-400/15 px-5 sm:px-7 py-6" data-id="element-1328" data-mp="2nid0qab7gsnp">
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-5 items-end" data-id="element-1329" data-mp="2ijtc225892rn">
              <div data-id="element-1330" data-mp="2xidme2wjrkv2">
                <p className="font-serif text-xl sm:text-2xl text-white mb-2" data-id="element-1331" data-mp="2xgss8ee5a3xq">
                  Court Rush by Lahore Treasury
                </p>
                <p className="text-white/60 text-sm font-light leading-relaxed max-w-2xl" data-id="element-1332" data-mp="1342x5tc1ioor">
                  A fast, viral 15-minute Misl challenge: rally five people,
                  open Court Packets, awaken three memories, and compete for
                  free early access to the Lahore Court Preview at Illuminarium
                  Toronto.
                </p>
                <p className="mt-3 text-amber-400/75 text-[10px] tracking-[0.22em] uppercase" data-id="element-1333" data-mp="3eqmwbjz7en0f">
                  Start a Misl. Rally your people. Open the Court Gates.
                </p>
              </div>

              <button type="button" onClick={event => {
              event.preventDefault();
              downloadText('court-rush-brief.md', COURT_RUSH_BRIEF_MD, 'text/markdown;charset=utf-8');
            }} className="inline-flex items-center justify-center gap-2 border border-amber-400/45 hover:border-amber-400 hover:bg-amber-400/10 text-amber-400 px-5 py-3 text-[10px] tracking-[0.22em] uppercase font-medium transition-colors" data-id="element-1334" data-mp="34zjwsmlxag39">
                <Download className="w-3.5 h-3.5" data-id="element-1335" data-mp="kambzfz64jyq" />
                Download Brief
              </button>
            </div>

            <div className="mt-6 pt-5 border-t border-amber-400/10 grid grid-cols-1 sm:grid-cols-3 gap-3 text-[11px] font-light" data-id="element-1336" data-mp="2bffuqo7pyvs4">
              <p className="text-white/50" data-id="element-1337" data-mp="27d2ozzsz2xvc">
                <span className="block text-amber-400/80 text-[9px] tracking-[0.2em] uppercase mb-1" data-id="element-1338" data-mp="27fyovtodrwmf">
                  Core loop
                </span>
                5 people · 10 card reveals · 3 memories
              </p>
              <p className="text-white/50" data-id="element-1339" data-mp="1soy6owxu0od3">
                <span className="block text-amber-400/80 text-[9px] tracking-[0.2em] uppercase mb-1" data-id="element-1340" data-mp="1so695gkocgk6">
                  Reward
                </span>
                Up to 10 free preview seats per winning Misl
              </p>
              <p className="text-white/50" data-id="element-1341" data-mp="1jm6rcm1u58g6">
                <span className="block text-amber-400/80 text-[9px] tracking-[0.2em] uppercase mb-1" data-id="element-1342" data-mp="1epq6c0pxna7b">
                  Scope
                </span>
                19 screens · viral V1 · no discounts or campaign mechanics
              </p>
            </div>
          </div>
        </motion.details>

        {/* Logo & Copyright */}
        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="text-center border-t border-amber-400/30 pt-12" data-id="element-1343" data-mp="3qayzdkw6hoop">
          <motion.div initial={{
          opacity: 0,
          scale: 0.8
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="flex justify-center mb-8" data-id="element-1344" data-mp="37m25vj8lv07j">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/aqXvi2nxLJFgni6jP9pU4X/image.png" alt="Sikh Kingdoms Logo" className="w-32 h-32 object-cover rounded-full opacity-80" data-id="element-1345" data-mp="2ny1q6zz9vxip" />
          </motion.div>
          <p className="text-white/40 text-xs tracking-wider uppercase font-light" data-id="element-1346" data-mp="3m9m5now8lhzq">
            {t.copyright}
            <br data-id="element-1347" data-mp="4vgzba8v5cra" />
            {t.allRightsReserved}
          </p>
        </motion.div>
      </div>
    </footer>;
};